import { isoToMuiDatepickerDate, startOfDayToUtcMillis } from 'shared/utils/date/dates';

interface DateRangeValue {
  start: number | null;
  end: number | null;
}

export interface DocumentationFilterItem {
  field?: string;
  operator?: string;
  value?: unknown;
}

export interface DocumentationFilterModel {
  items?: DocumentationFilterItem[];
  logicOperator?: 'and' | 'or' | 'And' | 'Or';
}

export interface EntityWithId {
  _id: string;
}

export type DocumentationFilterCellValue =
  | string
  | number
  | boolean
  | Date
  | EntityWithId[]
  | null
  | undefined;

export interface MatchesDocumentationFilterOptions {
  fixedFilterField?: string;
  fixedFilterValue?: unknown;
}

function isDateRangeValue(value: unknown): value is DateRangeValue {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'start' in value &&
      'end' in value
  );
}

function isEntityWithIdArray(value: unknown): value is EntityWithId[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item != null &&
        typeof item === 'object' &&
        typeof Reflect.get(item, '_id') === 'string'
    )
  );
}

function toComparableString(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).toLowerCase();
}

function matchesStringOperator(
  operator: string,
  filterValue: unknown,
  cellValue: DocumentationFilterCellValue
): boolean {
  const normalizedCell = toComparableString(cellValue);

  switch (operator) {
    case 'contains':
      return normalizedCell.includes(toComparableString(filterValue));
    case 'doesNotContain':
      return !normalizedCell.includes(toComparableString(filterValue));
    case 'equals':
    case 'is':
      return normalizedCell === toComparableString(filterValue);
    case 'doesNotEqual':
    case 'not':
      return normalizedCell !== toComparableString(filterValue);
    case 'startsWith':
      return normalizedCell.startsWith(toComparableString(filterValue));
    case 'endsWith':
      return normalizedCell.endsWith(toComparableString(filterValue));
    case 'isEmpty':
      return normalizedCell.length === 0;
    case 'isNotEmpty':
      return normalizedCell.length > 0;
    case 'isAnyOf': {
      if (!Array.isArray(filterValue)) {
        return true;
      }
      const values = filterValue.map((value) => toComparableString(value));
      return values.includes(normalizedCell);
    }
    default:
      return true;
  }
}

function matchesNumericOperator(
  operator: string,
  filterValue: unknown,
  cellValue: DocumentationFilterCellValue
): boolean {
  const filterNumber = Number(filterValue);
  const cellNumber = Number(cellValue);

  if (Number.isNaN(filterNumber) || Number.isNaN(cellNumber)) {
    return true;
  }

  switch (operator) {
    case '>':
    case 'gt':
      return cellNumber > filterNumber;
    case '<':
    case 'lt':
      return cellNumber < filterNumber;
    case '>=':
    case 'gte':
      return cellNumber >= filterNumber;
    case '<=':
    case 'lte':
      return cellNumber <= filterNumber;
    case '=':
    case 'equals':
      return cellNumber === filterNumber;
    case '!=':
    case 'doesNotEqual':
      return cellNumber !== filterNumber;
    default:
      return true;
  }
}

function matchesDateOperator(
  operator: string,
  filterValue: unknown,
  cellValue: DocumentationFilterCellValue,
  timezone: string
): boolean {
  if (
    operator !== 'dateRange' &&
    operator !== 'dateRangePicker'
  ) {
    return true;
  }

  if (!isDateRangeValue(filterValue) || !(cellValue instanceof Date)) {
    return false;
  }

  const { start, end } = filterValue;
  if (start === null && end === null) {
    return true;
  }

  const dateInTimezone = isoToMuiDatepickerDate(cellValue.toISOString(), timezone);
  if (!dateInTimezone) {
    return false;
  }

  const dayStartInUtc = startOfDayToUtcMillis(dateInTimezone);
  if (!dayStartInUtc) {
    return false;
  }

  const afterStart = start === null || dayStartInUtc >= start;
  const beforeEnd = end === null || dayStartInUtc <= end;

  return afterStart && beforeEnd;
}

function matchesMultiSelectOperator(
  operator: string,
  filterValue: unknown,
  cellValue: DocumentationFilterCellValue
): boolean {
  if (operator !== 'contains') {
    return true;
  }

  if (!Array.isArray(filterValue)) {
    return true;
  }

  if (!isEntityWithIdArray(cellValue)) {
    return filterValue.length === 0;
  }

  if (filterValue.length === 0) {
    return true;
  }

  const selectedIds = new Set(
    filterValue.filter((value): value is string => typeof value === 'string')
  );

  return cellValue.some((item) => selectedIds.has(item._id));
}

function resolveFieldValueType(field: string):
  | 'string'
  | 'numeric'
  | 'date'
  | 'single-select'
  | 'multi-select' {
  switch (field) {
    case 'state':
      return 'single-select';
    case 'createdAt':
    case 'modifiedAt':
      return 'date';
    case 'relatedIssuesCount':
      return 'numeric';
    case 'createdBy':
    case 'modifiedBy':
    case 'sites':
    case 'levels':
      return 'multi-select';
    default:
      return 'string';
  }
}

function matchesFilterItem(
  field: string,
  operator: string,
  filterValue: unknown,
  cellValue: DocumentationFilterCellValue,
  timezone: string
): boolean {
  const valueType = resolveFieldValueType(field);

  switch (valueType) {
    case 'numeric':
      return matchesNumericOperator(operator, filterValue, cellValue);
    case 'date':
      return matchesDateOperator(operator, filterValue, cellValue, timezone);
    case 'single-select':
      return matchesStringOperator(operator, filterValue, cellValue);
    case 'multi-select':
      return matchesMultiSelectOperator(operator, filterValue, cellValue);
    case 'string':
    default:
      return matchesStringOperator(operator, filterValue, cellValue);
  }
}

export function matchesDocumentationFilter<T>(
  item: T,
  filterModel: DocumentationFilterModel,
  getCellValue: (field: string, row: T) => DocumentationFilterCellValue,
  timezone: string,
  options: MatchesDocumentationFilterOptions = {}
): boolean {
  const items = filterModel?.items || [];
  const { fixedFilterField } = options;
  const hasFixedFilterValue =
    Boolean(fixedFilterField) && Object.prototype.hasOwnProperty.call(options, 'fixedFilterValue');

  const predicate = (filterItem: DocumentationFilterItem): boolean => {
    const field = filterItem.field || '';
    const operator = filterItem.operator || 'contains';
    const filterValue = filterItem.value;
    const cellValue = getCellValue(field, item);

    return matchesFilterItem(field, operator, filterValue, cellValue, timezone);
  };

  const fixedItems = fixedFilterField
    ? items.filter((item) => item.field === fixedFilterField)
    : [];
  const regularItems = fixedFilterField
    ? items.filter((item) => item.field !== fixedFilterField)
    : items;

  if (hasFixedFilterValue && fixedFilterField) {
    const fixedCellValue = getCellValue(fixedFilterField, item);
    if (!matchesFilterItem(fixedFilterField, 'is', options.fixedFilterValue, fixedCellValue, timezone)) {
      return false;
    }
  }

  if (fixedItems.length > 0 && !fixedItems.every(predicate)) {
    return false;
  }

  if (regularItems.length === 0) {
    return true;
  }

  const logicOperator = filterModel.logicOperator || 'and';
  if (logicOperator === 'Or' || logicOperator === 'or') {
    return regularItems.some(predicate);
  }

  return regularItems.every(predicate);
}
